import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Import module components
import ModuleDiagrams from './ModuleDiagrams';
import AssessmentDiagrams from './AssessmentDiagrams';
import InterventionDiagrams from './InterventionDiagrams';
import AssessmentProcessGuide from './AssessmentProcessGuide';

// Import quiz components
import StatisticsQuiz from './quizzes/StatisticsQuiz';
import AssessmentQuiz from './quizzes/AssessmentQuiz';
import InterventionQuiz from './quizzes/InterventionQuiz';
import ImplementationQuiz from './quizzes/ImplementationQuiz';

const QuickTipCard = ({ title, content }) => (
  <Card sx={{ mb: 2, backgroundColor: 'primary.light', color: 'white' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <InfoIcon sx={{ mr: 1 }} />
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Typography variant="body2">{content}</Typography>
    </CardContent>
  </Card>
);

const ResourceDownloadCard = ({ title, description, filename }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        <Tooltip title="Download Resource">
          <IconButton color="primary">
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </CardContent>
  </Card>
);

const ModuleProgress = ({ completed, total }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="body2" color="text.secondary">Module Progress</Typography>
      <Typography variant="body2" color="text.secondary">{completed}/{total} completed</Typography>
    </Box>
    <LinearProgress 
      variant="determinate" 
      value={(completed/total) * 100}
      sx={{ height: 8, borderRadius: 4 }}
    />
  </Box>
);

const ModuleLayout = () => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [quizCompleted, setQuizCompleted] = useState({});

  const handleQuizComplete = useCallback((stepIndex) => {
    console.log(`Quiz ${stepIndex} completed`);
    setQuizCompleted(prev => {
      const newState = { ...prev, [stepIndex]: true };
      console.log('Updated quiz state:', newState);
      return newState;
    });
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStep]);

  // Debug effect to monitor quiz completion state
  useEffect(() => {
    console.log('Quiz completion state updated:', quizCompleted);
  }, [quizCompleted]);
  
  const handleNext = () => {
    console.log('Current quiz state:', quizCompleted);
    console.log('Active step:', activeStep);
    console.log('Is quiz completed?', quizCompleted[activeStep]);

    if (quizCompleted[activeStep]) {
      const nextStep = activeStep + 1;
      setCompletedSteps(prev => new Set([...prev, activeStep]));
      setActiveStep(nextStep);
      console.log('Moving to step:', nextStep);
      
      // Scroll to top after a short delay to ensure the new content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      alert('Please complete the quiz before proceeding.');
    }
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);
  };

  const handleStepClick = (index) => {
    if (index < activeStep || quizCompleted[activeStep]) {
      setActiveStep(index);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      alert('Please complete the current section\'s quiz before proceeding.');
    }
  };

  const renderContent = () => (
    <Box ref={contentRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '600px' }}>
      <Box sx={{ flex: 1, mb: 4 }}>
        {modules[activeStep].content}
      </Box>
      <Box 
        sx={{ 
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          mt: 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 1,
        }}
      >
        <Button
          onClick={handleBack}
          sx={{ mr: 1 }}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={activeStep === modules.length - 1 || !quizCompleted[activeStep]}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );

  const modules = [
    {
      label: 'Fall Risk Statistics',
      content: (
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
            Understanding Fall Risk Patterns
          </Typography>
          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Fall risk increases with age. 25% of seniors fall yearly, with higher rates in 80+ age group. Prevention is key.
            </Typography>
          </Paper>
          <Box sx={{ mb: 4 }}>
            <ModuleDiagrams />
          </Box>
          <StatisticsQuiz onComplete={() => handleQuizComplete(0)} />
        </Box>
      )
    },
    {
      label: 'Risk Assessment',
      content: (
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
            Assessment Methods and Tools
          </Typography>
          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              TUG Test measures balance. Regular assessments track changes and guide interventions. High-risk cases need quarterly checks.
            </Typography>
          </Paper>
          <Box sx={{ mb: 4 }}>
            <AssessmentProcessGuide />
          </Box>
          <AssessmentQuiz onComplete={() => handleQuizComplete(1)} />
        </Box>
      )
    },
    {
      label: 'Prevention Plan',
      content: (
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
            Intervention Strategies
          </Typography>
          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Interventions target specific risks. Start with highest impact measures. Adjust based on progress and reassessment.
            </Typography>
          </Paper>
          <Box sx={{ mb: 4 }}>
            <InterventionDiagrams />
          </Box>
          <InterventionQuiz onComplete={() => handleQuizComplete(2)} />
        </Box>
      )
    },
    {
      label: 'Implementation',
      content: (
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
            Resources and Guidelines
          </Typography>
          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Download guides for assessment and implementation. Follow step-by-step instructions for best results.
            </Typography>
          </Paper>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mb: 4 }}>
            <ResourceDownloadCard
              title="Assessment Guide"
              description="Comprehensive evaluation steps"
              filename="assessment_checklist.pdf"
            />
            <ResourceDownloadCard
              title="Implementation Plan"
              description="Detailed action steps"
              filename="intervention_protocol.pdf"
            />
          </Box>
          <ImplementationQuiz onComplete={() => handleQuizComplete(3)} />
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h3" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 2 }}>
          Fall Prevention and Safety
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          A comprehensive guide to understanding, assessing, and preventing falls in elderly care settings.
        </Typography>
        <ModuleProgress completed={completedSteps.size} total={modules.length} />
      </Paper>
      
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'white', height: 'fit-content' }}>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ minWidth: 300 }}>
            {modules.map((module, index) => (
              <Step
                key={index}
                completed={completedSteps.has(index)}
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: 'pointer',
                  '& .MuiStepLabel-root': {
                    padding: '16px 8px',
                  },
                  '& .MuiStepLabel-label': {
                    fontWeight: 500,
                  },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                  },
                }}
              >
                <StepLabel>
                  <Typography variant="subtitle1">{module.label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Paper elevation={2} sx={{ flex: 1, p: 4, borderRadius: 2, bgcolor: 'white' }}>
          {renderContent()}
        </Paper>
      </Box>
    </Box>
  );
};

export default ModuleLayout;
